import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, InputNumber, Input, Button, Typography, Row, Col, Segmented } from 'antd';
import { useCurrency } from '../../hooks/useCurrency';
import { CreateShiftClosingDto, CashDenominationCountDto } from '../../types/cashRegister';

const { TextArea } = Input;
const { Text } = Typography;

// Mexican peso denominations
const BILL_DENOMINATIONS = [1000, 500, 200, 100, 50, 20];
const COIN_DENOMINATIONS = [20, 10, 5, 2, 1, 0.5];

interface DenominationCount {
  [key: number]: number;
}

interface CreateShiftClosingModalProps {
  visible: boolean;
  cashRegisterId: string;
  onCancel: () => void;
  onSubmit: (values: CreateShiftClosingDto) => void;
  loading?: boolean;
}

/**
 * Cierre de turno = conteo CIEGO: el empleado declara lo contado sin ver el
 * esperado del sistema (la comparación la hace el admin en la revisión).
 * Por eso, a diferencia del cierre de caja, aquí no hay hero de diferencia.
 */
const CreateShiftClosingModal: React.FC<CreateShiftClosingModalProps> = ({
  visible,
  cashRegisterId,
  onCancel,
  onSubmit,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const { currencySymbol } = useCurrency();

  // Mexican peso denominations
  const billDenominations = BILL_DENOMINATIONS;
  const coinDenominations = COIN_DENOMINATIONS;

  // State for denomination counts
  const [billCounts, setBillCounts] = useState<DenominationCount>({});
  const [coinCounts, setCoinCounts] = useState<DenominationCount>({});
  const [useDetailedCount, setUseDetailedCount] = useState<boolean>(true);
  const [directCashAmount, setDirectCashAmount] = useState<number>(0);

  const moneyStyle: React.CSSProperties = {
    fontFamily: 'ui-monospace, Consolas, monospace',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  };

  // Calculate totals
  const billsTotal = useMemo(() => {
    return Object.entries(billCounts).reduce((sum, [denom, count]) => {
      return sum + (Number(denom) * (count || 0));
    }, 0);
  }, [billCounts]);

  const coinsTotal = useMemo(() => {
    return Object.entries(coinCounts).reduce((sum, [denom, count]) => {
      return sum + (Number(denom) * (count || 0));
    }, 0);
  }, [coinCounts]);

  const totalCash = useMemo(() => billsTotal + coinsTotal, [billsTotal, coinsTotal]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setBillCounts({});
      setCoinCounts({});
      setUseDetailedCount(true);
      setDirectCashAmount(0);
      // Set countedCash to 0 initially, user will update via denomination counting
      form.setFieldValue('countedCash', 0);
    }
  }, [visible, form]);

  // Update countedCash field when totals change
  useEffect(() => {
    if (useDetailedCount) {
      form.setFieldValue('countedCash', totalCash);
    } else {
      form.setFieldValue('countedCash', directCashAmount);
    }
  }, [totalCash, directCashAmount, useDetailedCount, form]);

  const handleModeChange = (detailed: boolean) => {
    setUseDetailedCount(detailed);
    if (detailed) {
      // Switching to detailed count - use calculated total
      form.setFieldValue('countedCash', totalCash);
    } else {
      // Switching to direct input - use direct amount
      form.setFieldValue('countedCash', directCashAmount);
    }
  };

  const handleDirectAmountChange = (value: number | null) => {
    setDirectCashAmount(value || 0);
  };

  const handleBillCountChange = (denomination: number, count: number | null) => {
    setBillCounts(prev => ({
      ...prev,
      [denomination]: count || 0,
    }));
  };

  const handleCoinCountChange = (denomination: number, count: number | null) => {
    setCoinCounts(prev => ({
      ...prev,
      [denomination]: count || 0,
    }));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Build cash breakdown from denomination counts (only if using detailed count)
      let cashBreakdown: CashDenominationCountDto[] | undefined = undefined;
      let finalCashAmount = directCashAmount;

      if (useDetailedCount) {
        cashBreakdown = [];
        finalCashAmount = totalCash;

        // Add bills
        billDenominations.forEach(denom => {
          const count = billCounts[denom] || 0;
          if (count > 0) {
            cashBreakdown!.push({
              denomination: denom,
              quantity: count,
              total: denom * count,
            });
          }
        });

        // Add coins
        coinDenominations.forEach(denom => {
          const count = coinCounts[denom] || 0;
          if (count > 0) {
            cashBreakdown!.push({
              denomination: denom,
              quantity: count,
              total: denom * count,
            });
          }
        });

        if (cashBreakdown.length === 0) {
          cashBreakdown = undefined;
        }
      }

      // Submit with cash breakdown
      onSubmit({
        ...values,
        countedCash: finalCashAmount,
        cashBreakdown,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, paddingRight: 28 }}>
          <span>Cerrar turno</span>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            declara lo contado; el administrador lo revisa después
          </Text>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Cerrar turno
        </Button>,
      ]}
      width={640}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          countedCash: 0,
          countedCardSales: 0,
          countedTransferSales: 0,
          countedOtherSales: 0,
          countedTips: 0
        }}
      >
        {/* Efectivo: modo como Segmented, mismo patrón que el cierre de caja */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <Segmented
            value={useDetailedCount ? 'detailed' : 'direct'}
            onChange={(value) => handleModeChange(value === 'detailed')}
            options={[
              { label: 'Contar billetes y monedas', value: 'detailed' },
              { label: 'Ingresar total', value: 'direct' },
            ]}
          />
          <div style={{ marginLeft: 'auto', fontSize: 13 }}>
            Efectivo contado{' '}
            <span style={{ ...moneyStyle, fontSize: 17 }}>
              {currencySymbol}{(useDetailedCount ? totalCash : directCashAmount).toFixed(2)}
            </span>
          </div>
        </div>

        {useDetailedCount ? (
          <div style={{ marginBottom: 8 }}>
            {/* Denominaciones en grid neutral: denominación × cantidad = subtotal */}
            {[
              { title: 'Billetes', denoms: billDenominations, counts: billCounts, onChange: handleBillCountChange },
              { title: 'Monedas', denoms: coinDenominations, counts: coinCounts, onChange: handleCoinCountChange },
            ].map((group) => (
              <div key={group.title} style={{ marginBottom: 10 }}>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                  {group.title}
                  <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                    {currencySymbol}
                    {(group.title === 'Billetes' ? billsTotal : coinsTotal).toFixed(2)}
                  </Text>
                </Text>
                <Row gutter={[8, 8]}>
                  {group.denoms.map(denom => (
                    <Col xs={12} sm={8} key={`${group.title}-${denom}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text style={{ ...moneyStyle, minWidth: 48, fontSize: 13, fontWeight: 500 }}>
                          {currencySymbol}{denom < 1 ? denom.toFixed(2) : denom.toLocaleString()}
                        </Text>
                        <InputNumber
                          size="small"
                          min={0}
                          value={group.counts[denom] || 0}
                          onChange={(val) => group.onChange(denom, val)}
                          style={{ width: 58 }}
                        />
                        <Text type="secondary" style={{ ...moneyStyle, fontSize: 11, fontWeight: 400 }}>
                          = {currencySymbol}{((group.counts[denom] || 0) * denom).toFixed(denom < 1 ? 2 : 0)}
                        </Text>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            ))}
          </div>
        ) : (
          /* Direct Input Mode */
          <Form.Item
            label="Total de efectivo contado"
            help="El total de efectivo físico en caja"
          >
            <InputNumber
              style={{
                width: 240,
                maxWidth: '100%',
                fontFamily: 'ui-monospace, Consolas, monospace',
                fontSize: 18,
                fontWeight: 650,
              }}
              placeholder="0.00"
              min={0}
              precision={2}
              step={10}
              value={directCashAmount}
              onChange={handleDirectAmountChange}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
              addonBefore={currencySymbol}
            />
          </Form.Item>
        )}

        {/* Hidden field for countedCash - populated from denomination counting */}
        <Form.Item name="countedCash" hidden>
          <InputNumber />
        </Form.Item>

        <div style={{ borderTop: '1px dashed var(--border-color)', paddingTop: 10 }}>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
            Otras formas de pago
          </Text>

          <Row gutter={12}>
            <Col xs={12} sm={6}>
              <Form.Item
                label="Tarjeta"
                name="countedCardSales"
                rules={[
                  { required: true, message: 'Requerido' },
                  { type: 'number', min: 0, message: 'Debe ser >= 0' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                  prefix={currencySymbol}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                label="Transferencias"
                name="countedTransferSales"
                rules={[
                  { required: true, message: 'Requerido' },
                  { type: 'number', min: 0, message: 'Debe ser >= 0' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                  prefix={currencySymbol}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                label="Otras ventas"
                name="countedOtherSales"
                rules={[
                  { required: true, message: 'Requerido' },
                  { type: 'number', min: 0, message: 'Debe ser >= 0' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                  prefix={currencySymbol}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                label="Propinas"
                name="countedTips"
                rules={[
                  { required: true, message: 'Requerido (0 si no hay)' },
                  { type: 'number', min: 0, message: 'Debe ser >= 0' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                  prefix={currencySymbol}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item label="Notas (opcional)" name="employeeNotes">
          <TextArea
            rows={2}
            placeholder="Cualquier observación sobre tu turno…"
            maxLength={1000}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateShiftClosingModal;
